import type { Sponsor } from 'entities/sponsors'
import { createCleanParams } from 'shared/lib'
import { baseApi } from 'shared/api/baseApi'
import {
    APPLICATION_COMPETITION_TAG,
    COMPETITION_DOCUMENTS_TAG,
    COMPETITION_TAG,
    SINGLE_COMPETITION_TAG,
} from 'shared/api/tags'
import {
    resToCompetitions,
    transformCompetitionFormUpdateToReq,
    transformToReq,
} from '../model/transformations/useTransformResponse'
import type { CompetitionReportResponse } from '../model/transformations/buildCompetitionReportDocument'
import type {
    Competition,
    CompetitionDocumentRequestBody,
    CompetitionIUrlResponse,
    CompetitionRequestBody,
    CompetitionResponseData,
    CompetitionResponseParams,
} from '../model/types'
import { createSerializedKey, mergeCache, queryMappings } from '../model/consts'

export const competitionsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        // Single endpoint for all competition types
        getCompetitions: build.query<
            DataWithMeta<Competition>,
            CompetitionResponseParams
        >({
            query: (params) => ({
                url: `competitions`,
                method: 'GET',
                params: createCleanParams(params as Record<string, unknown>),
            }),
            transformResponse: (response: unknown) =>
                resToCompetitions(
                    response as DataWithMeta<Competition>
                ) as unknown as DataWithMeta<Competition>,

            // Custom serialization based on status to maintain separate caches
            serializeQueryArgs: ({ queryArgs }) => {
                const { statuses, organizationIds, unitIds, search } = queryArgs
                return `competitions-${statuses}-${organizationIds || 'all'}-${unitIds || 'all'}-${search || 'no-search'}`
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                if (!previousArg) return true

                // Refetch if key parameters change
                return (
                    currentArg?.organizationIds !==
                        previousArg.organizationIds ||
                    currentArg?.unitIds !== previousArg.unitIds ||
                    currentArg?.search !== previousArg.search ||
                    (currentArg?.page === 1 && previousArg.page !== 1)
                )
            },

            providesTags: (_result, _error, arg) => [
                { type: COMPETITION_TAG, id: arg.statuses },
                APPLICATION_COMPETITION_TAG,
            ],
        }),

        /**
         * Plain, page-aware competitions list for search pickers.
         *
         * `getCompetitions` keys its cache on statuses/org/unit/search and
         * deliberately leaves `page` out, and its `forceRefetch` only fires
         * when those change — so asking it for page 2 returns page 1 from
         * cache. This endpoint keeps the default per-arg caching so a picker
         * can walk pages as the menu is scrolled.
         */
        searchCompetitions: build.query<
            DataWithMeta<Competition>,
            CompetitionResponseParams
        >({
            query: (params) => ({
                url: `competitions`,
                method: 'GET',
                params: createCleanParams(params as Record<string, unknown>),
            }),
            providesTags: [COMPETITION_TAG],
        }),

        getOngoingCompetitions: build.query<
            DataWithMeta<Competition>,
            CompetitionResponseParams
        >({
            query: (params) => ({
                url: `competitions`,
                method: 'GET',
                params: createCleanParams(params as Record<string, unknown>),
            }),
            transformResponse: (response: unknown) =>
                resToCompetitions(
                    response as DataWithMeta<Competition>
                ) as unknown as DataWithMeta<Competition>,

            providesTags: [COMPETITION_TAG],
        }),

        getFinishedCompetitions: build.query<
            DataWithMeta<Competition>,
            CompetitionResponseParams
        >({
            query: (params) => ({
                url: `competitions`,
                method: 'GET',
                params: createCleanParams(params as Record<string, unknown>),
            }),
            transformResponse: (response: unknown) =>
                resToCompetitions(
                    response as DataWithMeta<Competition>
                ) as unknown as DataWithMeta<Competition>,

            providesTags: [COMPETITION_TAG],
        }),

        getApplicationsCompetitions: build.query<
            DataWithMeta<Competition>,
            CompetitionResponseParams
        >({
            query: (params) => ({
                url: `competitions`,
                method: 'GET',
                params: createCleanParams(params as Record<string, unknown>),
            }),

            transformResponse: (response: unknown) =>
                resToCompetitions(
                    response as DataWithMeta<Competition>
                ) as unknown as DataWithMeta<Competition>,

            providesTags: [APPLICATION_COMPETITION_TAG],
        }),

        addCompetition: build.mutation<Competition, CompetitionRequestBody>({
            query: (body) => ({
                url: `competitions`,
                method: 'POST',
                body: transformToReq(body as unknown as Competition),
            }),
            invalidatesTags: [COMPETITION_TAG, APPLICATION_COMPETITION_TAG],
        }),

        getCompetition: build.query<
            Competition & { sponsors: Sponsor[] },
            CompetitionResponseParams & { id: string }
        >({
            query: (params) => ({
                url: `competitions/${params.competitionId}`,
                method: 'GET',
                params: params,
            }),

            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                return createSerializedKey(
                    endpointName,
                    queryArgs as unknown as Record<string, unknown>,
                    queryMappings
                )
            },

            merge: (currentCache, newItems, { arg }) => {
                mergeCache(
                    arg as unknown as {
                        page?: number
                        [key: string]: unknown
                    },
                    currentCache as unknown as {
                        items: Array<{
                            id: string | number
                            [key: string]: unknown
                        }>
                        meta: Record<string, unknown>
                    },
                    newItems as unknown as {
                        items: Array<{
                            id: string | number
                            [key: string]: unknown
                        }>
                        meta: Record<string, unknown>
                    }
                )
            },

            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },

            providesTags: [COMPETITION_TAG],
        }),

        addCompetitionDocument: build.mutation<
            CompetitionResponseData,
            { id: string; body: CompetitionDocumentRequestBody }
        >({
            query: ({ id, body }) => ({
                url: `competitions/${id}/documents`,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: [COMPETITION_TAG, COMPETITION_DOCUMENTS_TAG],
        }),

        addComponentDocumentUrlUpload: build.mutation<
            CompetitionIUrlResponse,
            { id: string }
        >({
            query: (body) => ({
                url: `competitions/${body.id}/documents/generate-upload-url`,
                method: 'POST',
                body: body,
            }),
            invalidatesTags: [COMPETITION_TAG, COMPETITION_DOCUMENTS_TAG],
        }),

        getCompetitionDocuments: build.query<
            Array<Competition>,
            { params: { id: string } }
        >({
            query: ({ params }) => ({
                url: `competitions/${params.id}/documents`,
                method: 'GET',
            }),
            providesTags: [COMPETITION_DOCUMENTS_TAG],
        }),

        editCompetition: build.mutation<
            Competition,
            { id: string; body: CompetitionRequestBody }
        >({
            query: ({ id, ...body }) => ({
                url: `competitions/${id}`,
                method: 'PATCH',
                body: transformCompetitionFormUpdateToReq(
                    body as unknown as Competition
                ),
            }),
            invalidatesTags: [
                COMPETITION_TAG,
                APPLICATION_COMPETITION_TAG,
                SINGLE_COMPETITION_TAG,
            ],
        }),

        updateCompetitionDocument: build.mutation<
            CompetitionResponseData,
            {
                competitionId: string
                documentId: number
                body: Partial<Document>
            }
        >({
            query: ({ competitionId, documentId, body }) => ({
                url: `competitions/${competitionId}/documents/${documentId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: [
                COMPETITION_TAG,
                APPLICATION_COMPETITION_TAG,
                SINGLE_COMPETITION_TAG,
                COMPETITION_DOCUMENTS_TAG,
            ],
        }),

        getSingleCompetition: build.query<
            Competition,
            { competitionId: string }
        >({
            query: ({ competitionId }) => ({
                url: `competitions/${competitionId}`,
                method: 'GET',
            }),

            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },

            providesTags: [SINGLE_COMPETITION_TAG],
        }),

        deleteCompetition: build.mutation<
            Competition,
            { competitionId: string }
        >({
            query: ({ competitionId }) => ({
                url: `competitions/${competitionId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                COMPETITION_TAG,
                APPLICATION_COMPETITION_TAG,
                APPLICATION_COMPETITION_TAG,
            ],
        }),

        deleteCompetitionDocument: build.mutation<
            Competition,
            { competitionId: string; documentId: number }
        >({
            query: ({ competitionId, documentId }) => ({
                url: `competitions/${competitionId}/documents/${documentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [COMPETITION_TAG, COMPETITION_DOCUMENTS_TAG],
        }),

        getCompetitionReport: build.query<
            CompetitionReportResponse,
            { competitionId: string; unitId?: string }
        >({
            query: ({ competitionId, unitId }) => ({
                url: `competitions/${competitionId}/report`,
                method: 'GET',
                params: { unitId },
            }),
        }),
    }),
})

export const {
    useAddCompetitionMutation,
    useEditCompetitionMutation,
    useGetCompetitionQuery,
    useGetOngoingCompetitionsQuery,
    useGetFinishedCompetitionsQuery,
    useGetApplicationsCompetitionsQuery,
    useAddCompetitionDocumentMutation,
    useAddComponentDocumentUrlUploadMutation,
    useGetCompetitionDocumentsQuery,
    useUpdateCompetitionDocumentMutation,
    useGetSingleCompetitionQuery,
    useDeleteCompetitionMutation,
    useDeleteCompetitionDocumentMutation,
    useGetCompetitionsQuery,
    useSearchCompetitionsQuery,
    useGetCompetitionReportQuery,
    useLazyGetCompetitionReportQuery,
} = competitionsApi
